import { logger } from "../services/logger.ts";
import { formatMarketResolutionCountdown } from "./marketResolutionDisplay.ts";
import {
  Market as DisplayMarket, // Renamed to avoid conflict
  // MarketOutcome, // Potentially remove if MarketOption replaces it
  MarketCategory,
  MarketPool,
  DbMarketType, // Import new DB market type
  MarketOption, // Import new option type
  MarketOptionStatus, // Import new option status type
  MarketStatus, // <<< Added Import
  MarketPrivacy, // <<< Added Import
  EngineType,
} from "../types.ts";
import { supabase } from "./db.ts";

// Updated interface reflecting DB structure + joined options
export interface MarketDataFromDb {
  id: string;
  title: string;
  market_type?: DbMarketType; // NEW - Make optional for legacy data
  is_private: boolean;
  privacy?: 'public' | 'private';
  is_dynamic: boolean; // Always true now that fixed fee is removed
  category: MarketCategory;
  description: string;
  market_options?: MarketOption[]; // NEW: From joined table - Make optional
  total_bets: number;
  active_bets_count?: number; // Number of active bets (excluding cancelled)
  total_pool?: number; // Total pool size across all options
  total_payout?: number;
  image_url?: string;
  trading_end_time?: string | null;
  created_by: string;
  status: MarketStatus; // <<< UPDATED: Use MarketStatus type alias
  volume?: number | string;
  created_at?: string;
  updated_at?: string;
  total_volume?: number;
  unique_traders?: number;
  /** LMSR: legacy cumulative trade events */
  lmsr_trade_count?: number;
  /** LMSR: count_active_lmsr_positions RPC result */
  lmsr_open_positions_count?: number;
  recent_volume?: number;
  price_change_24h?: number | string;
  market_pools?: MarketPool[];
  market_stats?: Array<object>;
  top_outcome?: string;
  top_outcome_percentage?: number;
  is_watchlisted?: boolean;
  resolution_time?: string;
  short_code?: string | null; // Add short_code field
  // --- Add legacy fields optionally ---
  outcome_options?: Array<{name: string; yesBets?: number}> | string | null; // For legacy data
  resolution_date?: string; // Legacy field name for resolution_time
  is_verified?: boolean;
  is_featured?: boolean;
  winning_option_id?: number;
  rejection_reason?: string; // Add field for rejection reason by admin
  cancellation_reason?: string; // Add field for cancellation reason
  engine_type?: EngineType;
  lmsr_b?: number | null;
  fee_percentage?: number | null;
  creator_commission_rate?: number | null;
  affiliate_commission_rate?: number | null;
}

// Interface for options provided during creation
interface CreateOptionData {
  name: string;
  trading_end_time?: string | Date | null; // Optional end time for knockout options
  // Add other initial fields if needed (e.g., image_url)
}

// Updated interface for market creation data
interface CreateMarketData {
  title: string;
  market_type: DbMarketType; // Must be 'fixed' or 'knockout'
  is_private: boolean; // Separate flag for privacy
  is_dynamic: boolean; // Always true now that fixed fee is removed
  category: MarketCategory;
  description: string;
  options: CreateOptionData[];
  resolution_time?: string | Date;
  trading_end_time?: string | Date | null; // Only for 'fixed' market type
  image_url?: string;
  status?: MarketStatus; // <<< Change type from string to MarketStatus
  // access_code?: string; // Add if implementing access codes
}

export class MarketEngine {
  private readonly MARKETPLACE_SERVICE_FEE = 0.08; // 8%

  private getCategoryImage(category: MarketCategory): string {
    const images = {
      'Crypto': 'https://images.unsplash.com/photo-1518546305927-5a555bb7020d?q=80&w=300',
      'Politics': 'https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?q=80&w=300',
      'Sports': 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?q=80&w=300',
      'Entertainment': 'https://images.unsplash.com/photo-1603190287605-e6ade32fa852?q=80&w=300',
      'Science': 'https://images.unsplash.com/photo-1507668077129-56e32842fceb?q=80&w=300',
      'Economics': 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?q=80&w=300',
      'Gaming': 'https://images.unsplash.com/photo-1538481199705-c710c4e965fc?q=80&w=300',
      'Technology': 'https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=300',
      'Social Media': 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?q=80&w=300',
      'Legal': 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=300',
      'Climate': 'https://images.unsplash.com/photo-1564088879-bf0ec252b8a8?q=80&w=300',
      'Health': 'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?q=80&w=300',
      'Business': 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=300',
      'AI': 'https://images.unsplash.com/photo-1677442136019-21780ecad995?q=80&w=300',
      'Other': 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?q=80&w=300',
    };
    return images[category] || images['Other'];
  }

  async addMarket(data: CreateMarketData) {
    try {
      if (!data.title?.trim()) {
        throw new Error('Market title is required');
      }
      if (!data.market_type || !['fixed', 'knockout'].includes(data.market_type)) {
        throw new Error('Market type must be either "fixed" or "knockout"');
      }
      if (!data.options || data.options.length < 2) {
        throw new Error('At least two options are required');
      }
      if (data.options.some(opt => !opt.name?.trim())) {
        throw new Error('All options must have a name');
      }

      const { data: user, error: userError } = await supabase.auth.getUser();
      if (userError || !user?.user) throw userError || new Error('User not found');

      // 1. Insert the market basics
      const marketInsertData: Partial<MarketDataFromDb> = {
        title: data.title.trim(),
        market_type: data.market_type, // Should be 'fixed' or 'knockout'
        is_private: data.is_private, // Set privacy flag
        is_dynamic: data.is_dynamic, // <<< ADDED: Set betting type flag
        category: data.category,
        description: data.description,
        resolution_time: data.resolution_time ? new Date(data.resolution_time).toISOString() : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        trading_end_time: data.market_type === 'fixed' && data.trading_end_time ? new Date(data.trading_end_time).toISOString() : null,
        created_by: user.user.id,
        status: data.status || 'active', // Now assigns MarketStatus | 'active'
        image_url: data.image_url
      };
      
      const { data: marketResult, error: marketError } = await supabase
        .from('markets')
        .insert(marketInsertData)
        .select('id') // Only select ID needed for options
        .single();

      if (marketError) throw marketError;
      if (!marketResult?.id) throw new Error('Failed to create market or retrieve ID.');

      const marketId = marketResult.id;

      // 2. Insert the options into market_options
      const optionsInsertData = data.options.map(opt => ({
        market_id: marketId,
        name: opt.name.trim(),
        // Set option-level trading_end_time only for knockout markets
        trading_end_time: data.market_type === 'knockout' && opt.trading_end_time
          ? new Date(opt.trading_end_time).toISOString()
          : null,
        status: 'active' as MarketOptionStatus // Default status
      }));

      const { error: optionsError } = await supabase
        .from('market_options')
        .insert(optionsInsertData);

      if (optionsError) {
        // Attempt to clean up the market if options fail
        logger.error('Error inserting market options, attempting to delete market:', optionsError);
        await supabase.from('markets').delete().eq('id', marketId);
        throw optionsError; // Re-throw the options error
      }

      // 3. Return the created market ID (or fetch the full market if needed)
      // Fetching the full market ensures we return the complete data structure
      return this.getMarket(marketId);

    } catch (error) {
      logger.error('Error adding market:', error);
      throw error;
    }
  }

  async getMarket(id: string): Promise<DisplayMarket | null> { // Return type updated
      const { data, error } = await supabase
        .from('markets')
        .select(`
          *,
          winning_option_id,
          market_options!market_id (*),
          market_pools (*)
        `)
        .eq('id', id)
        .single();

    if (error) {
      logger.error('Error fetching market data:', error);
      if (error.code === 'PGRST116') { 
          return null;
      }
      throw error; // Re-throw other errors
    }
     if (!data) return null;

    try {
      const typed = data as MarketDataFromDb;
      if (typed.engine_type === 'lmsr') {
        const { data: cnt, error: cntErr } = await supabase.rpc('count_active_lmsr_positions', {
          p_market_id: id,
        });
        if (!cntErr && cnt != null) {
          typed.lmsr_open_positions_count =
            typeof cnt === 'bigint' ? Number(cnt) : typeof cnt === 'number' ? cnt : Number(cnt ?? 0);
        }
      }
      return await this.toDisplayMarket(typed);
    } catch (err) {
      logger.error('toDisplayMarket failed for market fetch:', err);
      return null;
    }
  }

   async getAllMarkets(): Promise<DisplayMarket[]> { // Return type updated
    try {
      logger.info('Fetching all markets with market_options and market_pools...');
      const { data, error } = await supabase
        .from('markets')
        .select(`
          *,
          winning_option_id,
          market_options!market_id (*),
          market_pools (*)
        `)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      logger.info(`Retrieved ${data?.length || 0} markets from database`);
        
      // Map each market to the display type
      const displayMarkets = await Promise.all(
           (data || []).map(async market => {
             const result = await this.toDisplayMarket(market as MarketDataFromDb);
             // Log details about each market's options and pools
             if (result) {
               logger.info(`Market ${result.id} (${result.title}):`, {
                 optionsCount: result.market_options?.length || 0,
                 poolSize: result.totalPool,
                 hasOptions: !!result.market_options && result.market_options.length > 0,
                 topOption: result.market_options && result.market_options.length > 0 
                   ? result.market_options.sort((a, b) => (b.probability || 0) - (a.probability || 0))[0]
                   : null
               });
             }
             return result;
           })
      );
      // Filter out any null results from toDisplayMarket if errors occurred during mapping
      return displayMarkets.filter(market => market !== null) as DisplayMarket[];

    } catch (error) {
      logger.error('Error in getAllMarkets:', error);
      return [];
    }
  }

  // --- Calculation logic updated --- 

  calculatePricing(marketData: MarketDataFromDb, optionId: number, futureSize: number = 0) {
    if (!marketData || !Array.isArray(marketData.market_options)) {
        logger.error('Market data or options missing for pricing calculation.');
        return { yesPercentage: 0, noPercentage: 100, odds: 1, returnOnDollar: 1 };
    }

    const options = marketData.market_options;

    const option = options.find(opt => Number(opt.id) === Number(optionId));
    if (!option) {
        logger.warn(`Option with ID ${optionId} not found in market ${marketData.id} for pricing. Using default values.`);
        return { yesPercentage: 0, noPercentage: 100, odds: 1, returnOnDollar: 1 };
    }

    let probability = 0.5;

    if (marketData.engine_type === 'lmsr' && marketData.lmsr_b) {
        // LMSR pricing: p_i = exp(q_i / b) / Σ exp(q_j / b)
        const b = marketData.lmsr_b;
        const shares = options.map(o => Number(o.outstanding_shares ?? 0));
        const optIdx = options.findIndex(o => Number(o.id) === Number(optionId));
        if (optIdx >= 0 && shares.length > 0) {
            const exps = shares.map(s => Math.exp(s / b));
            const total = exps.reduce((a, v) => a + v, 0);
            probability = total > 0 ? exps[optIdx] / total : 1 / shares.length;
        } else if (options.length > 0) {
            probability = 1 / options.length;
        }
    } else {
        // Pool-based pricing
        const pools = Array.isArray(marketData.market_pools) ? marketData.market_pools : [];
        const currentTotalPool = pools.reduce((sum, pool) => sum + pool.total_amount, 0);
        const currentOptionPool = pools.find(pool => Number(pool.market_option_id) === Number(optionId))?.total_amount || 0;
        const futureTotalPool = currentTotalPool + futureSize;
        const futureOptionPool = currentOptionPool + futureSize;

        if (futureTotalPool > 0) {
            probability = futureOptionPool / futureTotalPool;
        } else if (options.length > 0) {
            probability = 1.0 / options.length;
        }
    }

    probability = Math.max(0, Math.min(1, probability));

    let odds = 1.0;
    if (probability > 0) {
        odds = probability < 0.000001 ? 999.99 : 1 / probability;
    } else {
        odds = 999.99;
    }

    const yesPercentage = probability * 100;
    const noPercentage = (1 - probability) * 100;

    return {
        yesPercentage,
        noPercentage,
        odds,
        returnOnDollar: odds,
    };
  }

  async toDisplayMarket(marketData: MarketDataFromDb): Promise<DisplayMarket | null> {
    // Check for essential data
    if (!marketData || !marketData.id) {
      logger.error("Invalid or incomplete market data passed to toDisplayMarket:", marketData);
      return null;
    }
    
    // Add debugging to see market data properties
    logger.debug(`toDisplayMarket for ${marketData.id} - market data properties:`, {
      ...marketData
    });

    // Determine market type (default to 'fixed' if missing)
    const marketType: DbMarketType = marketData.market_type || 'fixed';

    // Ensure market_options and market_pools are arrays or initialize empty arrays
    marketData.market_options = Array.isArray(marketData.market_options) ? marketData.market_options : [];
    marketData.market_pools = Array.isArray(marketData.market_pools) ? marketData.market_pools : [];

    // --- Prepare options: Use market_options if available, otherwise fallback to outcome_options --- 
    let rawOptions: MarketOption[] = [];
    if (marketData.market_options && marketData.market_options.length > 0) {
      logger.info(`Using market_options for market ${marketData.id}`);
      rawOptions = marketData.market_options;
    } else if (marketData.outcome_options) {
      logger.warn(`Market ${marketData.id} is using legacy outcome_options. Mapping...`);
      let legacyOptions: Array<{name: string; yesBets?: number}> = [];
      try {
        if (typeof marketData.outcome_options === 'string') {
          legacyOptions = JSON.parse(marketData.outcome_options);
        } else if (Array.isArray(marketData.outcome_options)) {
          legacyOptions = marketData.outcome_options;
        }

        if (!Array.isArray(legacyOptions)) {
          logger.error("Failed to parse legacy outcome_options into an array for market:", marketData.id);
          legacyOptions = []; // Prevent further errors
        }

        // Map legacy structure to new MarketOption structure
        rawOptions = legacyOptions.map((opt, index) => ({
          id: index, // Assign a temporary/mock ID (DB ID is missing)
          market_id: marketData.id,
          name: opt.name || `Legacy Option ${index + 1}`,
          trading_end_time: null, // Legacy options didn't have individual times
          status: 'active' as MarketOptionStatus, // Assume active
          created_at: marketData.created_at || new Date().toISOString(), // Use market creation or now
          updated_at: marketData.updated_at || new Date().toISOString(),
          yesBets: opt.yesBets || 0 // Carry over yesBets if present (though pools are preferred)
        }));
      } catch (error) {
        logger.error(`Error processing legacy outcome_options for market ${marketData.id}:`, error);
        // Leave rawOptions empty if parsing fails
      }
    } else {
      logger.error(`Market ${marketData.id} has neither market_options nor outcome_options.`);
      // Create at least one default option to prevent errors
      rawOptions = [{
        id: 0,
        market_id: marketData.id,
        name: "Default Option",
        trading_end_time: null,
        status: 'active' as MarketOptionStatus,
        created_at: marketData.created_at || new Date().toISOString(),
        updated_at: marketData.updated_at || new Date().toISOString()
      }];
    }
    // ---------------------------------------------------------------------------------------

    // Calculate pricing for each option, with improved error handling
    const processedOptions: MarketOption[] = rawOptions.map(option => {
      // Use status from the option object itself
      if (option.status === 'active' || option.status === 'pending_resolution') {
        try {
          // Pass option.id (might be the mock ID for legacy)
          const pricing = this.calculatePricing(marketData, option.id);
          return {
            ...option,
            probability: Math.round(pricing.yesPercentage * 100) / 100,
            odds: Math.round(pricing.odds * 100) / 100,
          };
        } catch (error) {
          logger.error(`Error calculating pricing for option ${option.id} (name: ${option.name}) in market ${marketData.id}:`, error);
          return {
            ...option,
            probability: 0,
            odds: 0
          };
        }
      } else {
        return {
          ...option,
          probability: option.status === 'resolved_winner' ? 100 : 0,
          odds: option.status === 'resolved_winner' ? 1 : 0
        };
      }
    });
      
    const resolutionRaw = marketData.resolution_time || marketData.resolution_date;
    const resolutionDateTime =
      resolutionRaw != null && String(resolutionRaw).trim() !== ''
        ? String(resolutionRaw)
        : undefined;
    const timeLeft = formatMarketResolutionCountdown(resolutionRaw ?? null);

    const poolSum = (marketData.market_pools || []).reduce(
      (sum: number, pool: MarketPool) => sum + (pool.total_amount || 0),
      0,
    );
    const vol = marketData.total_volume ?? marketData.volume;
    const totalPool =
      vol != null && Number(vol) > 0
        ? Number(vol)
        : poolSum > 0
          ? poolSum
          : Number(marketData.total_pool ?? 0);

    const sortedOptions = [...processedOptions].sort((a, b) => (b.probability ?? 0) - (a.probability ?? 0));
    const topOutcome = sortedOptions.length > 0 ? sortedOptions[0].name : '';
    const topOutcomePercentage = sortedOptions.length > 0 ? sortedOptions[0].probability ?? 0 : 0;

    const priceChange: number | string = marketData.price_change_24h ?? 'Not Available';
    
    // Create the display market with all properties from the DB
    const displayMarket = {
      id: marketData.id,
      title: marketData.title,
      market_type: marketType, // Use determined market type
      is_private: marketData.is_private,
      privacy: marketData.is_private ? 'private' as MarketPrivacy : 'public' as MarketPrivacy,
      is_dynamic: true, // Always true now
      description: marketData.description,
      image_url: marketData.image_url || this.getCategoryImage(marketData.category),
      category: marketData.category,
      market_options: processedOptions,
      total_bets: marketData.total_bets || 0,
      trading_end_time: marketData.trading_end_time, // Market level end time (mostly for fixed)
      resolution_time: resolutionDateTime,
      timeLeft,
      rules: marketData.description,
      status: marketData.status as MarketStatus,
      marketPools: marketData.market_pools as MarketPool[] || [],
      totalPool: totalPool,
      created_by: marketData.created_by,
      created_at: marketData.created_at,
      updated_at: marketData.updated_at,
      unique_traders: marketData.unique_traders || 0,
      lmsr_trade_count: marketData.lmsr_trade_count ?? 0,
      lmsr_open_positions_count: marketData.lmsr_open_positions_count ?? 0,
      recent_volume: marketData.recent_volume || 0,
      price_change_24h: priceChange,
      top_outcome: topOutcome,
      top_outcome_percentage: topOutcomePercentage,
      is_watchlisted: marketData.is_watchlisted || false,
      is_verified: marketData.is_verified || false,
      is_featured: marketData.is_featured || false,
      short_code: marketData.short_code, // Add short_code field
      winning_option_id: marketData.winning_option_id,
      rejection_reason: marketData.rejection_reason, // Add rejection reason field
      cancellation_reason: marketData.cancellation_reason, // Add cancellation reason field
      active_bets_count: marketData.active_bets_count || 0, // Add active_bets_count field
      engine_type: marketData.engine_type,
      lmsr_b: marketData.lmsr_b,
      fee_percentage: marketData.fee_percentage ?? null,
      creator_commission_rate: marketData.creator_commission_rate ?? null,
      affiliate_commission_rate: marketData.affiliate_commission_rate ?? null,
    };
    
    // Log the final market object
    logger.info(`Final displayMarket object for ${marketData.id}`);
    
    return displayMarket;
  }

  // --- Remove or adapt old calculation functions if they relied heavily on the old structure ---
  // async calculate24hPriceChange(...) - Needs rework based on new structure
  // async calculateRecentVolume(...) - Should still work if based on user_bets table


  // --- NEW Service functions to call DB procedures ---

  /**
   * Calls the DB function to disable active knockout options whose trading time has passed.
   * Should be called periodically (e.g., by a scheduled job or API endpoint).
   */
  async triggerDisableExpiredKnockoutOptions(): Promise<number> {
    const { data, error } = await supabase.rpc('disable_expired_knockout_options');

    if (error) {
      logger.error('Error calling disable_expired_knockout_options:', error);
      throw error;
    }
    logger.info(`Disabled ${data ?? 0} expired knockout options.`);
    return data ?? 0; // Returns the count of updated options
  }

  /**
   * Calls the DB function to resolve a specific match outcome within a knockout market.
   * @param marketId The ID of the market.
   * @param winnerOptionId The ID of the winning market_option.
   * @param loserOptionIds An array of IDs for the losing market_options.
   */
  async triggerResolveKnockoutMatch(
      marketId: string, // Market ID is usually string (UUID)
      winnerOptionId: number, // Option ID is number (bigint)
      loserOptionIds: number[] // Option IDs are numbers
    ): Promise<void> {

      if (!marketId || winnerOptionId == null || !loserOptionIds || loserOptionIds.length === 0) {
          throw new Error("Missing required parameters for resolving knockout match.");
      }

      const { error } = await supabase.rpc('resolve_knockout_match', {
        p_market_id: marketId, // Ensure correct mapping to function params
        p_winner_option_id: winnerOptionId,
        p_loser_option_ids: loserOptionIds
      });
      
      if (error) {
        logger.error('Error calling resolve_knockout_match:', error);
        throw error;
      }
      logger.info(`Successfully resolved match for market ${marketId}, winner: ${winnerOptionId}, losers: ${loserOptionIds.join(', ')}`);
  }

  async getMarketOptionHistory(marketId: string, timeRange: 'day' | 'week' | 'month' | 'all' = 'all'): Promise<Array<{
    timestamp: string;
    [optionName: string]: string | number;
  }>> {
    try {
      // Calculate the start time based on the timeRange
      let startTime: Date | null = null;
      const now = new Date();
      
      switch (timeRange) {
        case 'day':
          startTime = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case 'week':
          startTime = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startTime = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        default:
          startTime = null;
      }

      // Fetch the historical data
      const query = supabase
        .from('market_option_history')
        .select(`
          timestamp,
          probability,
          market_option_id,
          market_options!market_option_id (name)
        `)
        .eq('market_id', marketId)
        .order('timestamp', { ascending: true });

      // Add timeRange filter if specified
      if (startTime) {
        query.gte('timestamp', startTime.toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;

      // Group the data by timestamp
      const groupedData = data.reduce((acc: { [key: string]: { [key: string]: number } }, record: {
        timestamp: string;
        probability: number;
        market_option_id: number;
        market_options: { name: string };
      }) => {
        const timestamp = record.timestamp;
        if (!acc[timestamp]) {
          acc[timestamp] = {};
        }
        acc[timestamp][record.market_options.name] = record.probability;
        return acc;
      }, {});

      // Convert to array format expected by the graph component
      return Object.entries(groupedData).map(([timestamp, probabilities]) => ({
        timestamp,
        ...probabilities
      }));

    } catch (error) {
      logger.error('Error fetching market option history:', error);
      return [];
    }
  }

  async getMarketVolumeHistory(
    marketId: string,
    timeRange: 'day' | 'week' | 'month' | 'all' = 'all'
  ): Promise<Array<{ timestamp: string; [optionName: string]: string | number }>> {
    try {
      let startTime: Date | null = null;
      const now = new Date();
      switch (timeRange) {
        case 'day':
          startTime = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case 'week':
          startTime = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startTime = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        default:
          startTime = null;
      }

      const query = supabase
        .from('market_option_history')
        .select(`
          timestamp,
          volume,
          market_option_id,
          market_options!market_option_id (name)
        `)
        .eq('market_id', marketId)
        .order('timestamp', { ascending: true });

      if (startTime) {
        query.gte('timestamp', startTime.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;

      const groupedData = data.reduce(
        (
          acc: { [key: string]: { [key: string]: number } },
          record: {
            timestamp: string;
            volume: number | null;
            market_option_id: number;
            market_options: { name: string };
          }
        ) => {
          const timestamp = record.timestamp;
          if (!acc[timestamp]) acc[timestamp] = {};
          acc[timestamp][record.market_options.name] = Number(record.volume ?? 0);
          return acc;
        },
        {}
      );

      return Object.entries(groupedData).map(([timestamp, volumes]) => ({
        timestamp,
        ...volumes,
      }));
    } catch (error) {
      logger.error('Error fetching market volume history:', error);
      return [];
    }
  }

  /** Ensures each row includes every outcome name (0 when no trade on that outcome at that snapshot). */
  normalizeVolumeHistoryForChart(
    history: Array<{ timestamp: string; [key: string]: string | number }>,
    market: DisplayMarket
  ): Array<{ timestamp: string; [key: string]: string | number }> {
    const names = market.market_options?.map((o) => o.name) ?? [];
    if (names.length === 0) return history;
    return history
      .map((row) => {
        const out: Record<string, string | number> = { timestamp: row.timestamp };
        for (const n of names) {
          const v = row[n];
          if (v === undefined) out[n] = 0;
          else out[n] = typeof v === 'number' ? v : Number(v) || 0;
        }
        return out;
      })
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }

  /** Appends a synthetic "now" row from live option probabilities so the chart updates without waiting for history sync. */
  mergeOptionHistoryWithLiveMarket(
    history: Array<{ timestamp: string; [key: string]: string | number }>,
    market: DisplayMarket
  ): Array<{ timestamp: string; [key: string]: string | number }> {
    if (!market.market_options?.length) return history;
    const row: Record<string, string | number> = { timestamp: new Date().toISOString() };

    const active = market.market_options.filter(
      (o) => o.status === 'active' || o.status === 'pending_resolution'
    );

    if (market.engine_type === 'lmsr' && market.lmsr_b && active.length > 0) {
      const b = market.lmsr_b;
      const shares = active.map((o) => Number(o.outstanding_shares ?? 0));
      const exps = shares.map((s) => Math.exp(s / b));
      const total = exps.reduce((a, v) => a + v, 0);
      active.forEach((o, i) => {
        const pPct = total > 0 ? (exps[i] / total) * 100 : 100 / active.length;
        row[o.name] = Math.round(pPct * 100) / 100;
      });
    } else {
      for (const o of active) {
        row[o.name] = o.probability ?? 0;
      }
    }

    const merged = [...history, row];
    merged.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    return merged;
  }

}